// NOTE (tchu, 2025-10-10): Order matters here: we need to import katex.min.css before global.css
import 'katex/dist/katex.min.css';
import '../styles/globals.css';
import AppConfigProvider from '@modules/layout/layout/components/AppConfigProvider';
import AppRootLayout from '@modules/layout/layout/components/AppRootLayout';
import CurrentLocaleProvider from '@modules/localization/implementations/CurrentLocaleProvider';
import unifiedLoggerClient from '@modules/eventStream/implementations/unifiedLoggerClient';
import MarkdownComponentsProvider from '@modules/ui/markdown/components/MarkdownComponentsProvider';
import { getBEDEV1ServiceBasePath } from '@modules/utilities/constants/baseUrl';
import { RobloxAuthenticationProvider } from '@rbx/auth';
import { NavigationConfigsProvider } from '@rbx/creator-hub-navigation';
import { StaticTranslationProvider } from '@rbx/intl';
import { CacheProvider, SnackbarProvider, createCache, removeServerSideCSS } from '@rbx/ui';
import { createWebVitalsReporter } from '@rbx/web-vitals';
import { ErrorBoundary } from '@sentry/nextjs';
import React, { ReactNode, useEffect } from 'react';
import MultiProvider from '@modules/miscellaneous/common/components/MultiProvider';
import { IsAuthenticatedProvider } from '@modules/assistant/context/IsAuthenticatedProvider';
import AuthenticatedThemeModeProvider from '@modules/theme/hooks/AuthenticatedThemeModeProvider';
import {
  FeedbackDialogType,
  ShareFeedbackProvider,
} from '@modules/feedback/context/ShareFeedbackProvider';
import BrowserTabIdTrackingProvider from '@modules/miscellaneous/common/components/BrowserTabIdTrackingProvider';
import { createThumbnailsClient } from '@rbx/thumbnails';
import { StudioResourcesProvider } from '@rbx/studio';
import { createStudioResourcesWithTheme } from '@modules/useStudio/constants/studioResources';
import { AuthClient } from '@modules/clients/auth';
import { ApplicationAuthorizationsClient } from '@modules/clients/applicationAuthorization';
import { UsersClient } from '@modules/clients/users';
import { nonLandingPageMaxWidth } from '@modules/layout/layout/constants/layoutConstants';
import { NextPage } from 'next';
import { AppProps } from 'next/app';
import { useReportWebVitals } from 'next/web-vitals';
import {
  defaultLocale,
  defaultLocaleInfo,
} from '@modules/localization/constants/translationResourceConstants';
import { StaticPageProps } from '@modules/utilities/pageBuild/types/PageProps';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import PageTrackingProvider from '@modules/eventStream/providers/PageTrackingProvider';
import { SendToAssistantProvider } from '@modules/assistant/context/SendToAssistantProvider';
import NavigationSearchProvider from '@modules/layout/topNavigation/components/NavigationSearchProvider';
import { useThemeMode } from '@rbx/settings';

const { authenticationApi } = AuthClient;
const { discoveryApi } = ApplicationAuthorizationsClient;
const { usersApi } = UsersClient;

// reports custom NextJS metrics (Next.js-hydration, Next.js-route-change-to-render, Next.js-render)
export const reportWebVitals = createWebVitalsReporter(unifiedLoggerClient);

const clientSideEmotionCache = createCache();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
});

const getDefaultPageLayout = (
  page: ReactNode,
  props: StaticPageProps & { pageMaxWidth: number },
) => {
  const {
    translationResources = [],
    navigation = undefined,
    hideInPageNavigation = false,
    isOpenSourced = false,
    blockSearchIndexing = false,
    hideBreadcrumbs = false,
    breadcrumbs = [],
    menuData = [],
    pageMaxWidth = nonLandingPageMaxWidth,
    isLandingPage = false,
    data = {
      template: 'default',
      contentDirFilePath: '',
      hideSecondaryNav: false,
      machineTranslated: false,
    },
    locale = defaultLocale,
    localeInfo = defaultLocaleInfo,
  } = props;
  return (
    <MultiProvider
      providers={[
        /* eslint-disable react/jsx-key */
        <BrowserTabIdTrackingProvider />,
        <IsAuthenticatedProvider />,
        <CurrentLocaleProvider />,
        <StaticTranslationProvider localeInfo={localeInfo} resources={translationResources} />,
        <PageTrackingProvider pageProps={props} />,
        <AppConfigProvider
          isLandingPage={isLandingPage || data.template === 'landing-page'}
          breadcrumbs={breadcrumbs}
          isOpenSourced={isOpenSourced}
          blockSearchIndexing={blockSearchIndexing}
          menuData={menuData}
          hideInPageNavigation={hideInPageNavigation}
          hideBreadcrumbs={hideBreadcrumbs}
          contentDirFilePath={data.contentDirFilePath}
          locale={locale}
          pageMaxWidth={pageMaxWidth}
          hideSecondaryNav={!!data.hideSecondaryNav}
          machineTranslated={!!data.machineTranslated}
        />,
        <AppRootLayout navigation={navigation || undefined} urlBasePath='/' isStickyHeader />,
        <ShareFeedbackProvider feedbackDialogType={FeedbackDialogType.GeneralDocsFeedback} />,
        /* eslint-disable react/jsx-key */
      ]}>
      {page}
    </MultiProvider>
  );
};

createThumbnailsClient(getBEDEV1ServiceBasePath('thumbnails'));

export type NextPageWithLayout<P = object, IP = P> = NextPage<P, IP> & {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getPageLayout?: (page: ReactNode, pageProps: any) => ReactNode;
};

function AppWithTheme({
  Component,
  pageProps,
  getPageLayout,
}: {
  Component: NextPageWithLayout;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  pageProps: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getPageLayout: (page: ReactNode, pageProps: any) => ReactNode;
}) {
  const { themeMode } = useThemeMode();

  return (
    <SnackbarProvider>
      <NavigationConfigsProvider
        currentProduct='Documentation'
        environment={process.env.environment}
        target='global'>
        <MarkdownComponentsProvider>
          <StudioResourcesProvider resources={createStudioResourcesWithTheme(themeMode)}>
            <SendToAssistantProvider>
              <NavigationSearchProvider>
                {getPageLayout(
                  <Component {...pageProps} />,
                  pageProps as Parameters<typeof getPageLayout>[1],
                )}
              </NavigationSearchProvider>
            </SendToAssistantProvider>
          </StudioResourcesProvider>
        </MarkdownComponentsProvider>
      </NavigationConfigsProvider>
    </SnackbarProvider>
  );
}

export default function CustomApp({
  Component,
  pageProps,
  cache = clientSideEmotionCache,
}: AppProps & {
  Component: NextPageWithLayout;
  cache: React.ComponentProps<typeof CacheProvider>['cache'];
}) {
  const getPageLayout = Component.getPageLayout || getDefaultPageLayout;
  // reports core web vitals (TTFB, FCP, LCP, FID, CLS, INP)
  useReportWebVitals(createWebVitalsReporter(unifiedLoggerClient));

  useEffect(() => {
    removeServerSideCSS();
  }, []);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <CacheProvider cache={cache}>
          <RobloxAuthenticationProvider
            clientId={process.env.creatorClientId}
            authenticationClient={authenticationApi}
            discoveryClient={discoveryApi}
            usersClient={usersApi}>
            <AuthenticatedThemeModeProvider
              themeElement={typeof document !== 'undefined' ? document.documentElement : undefined}>
              <AppWithTheme
                Component={Component}
                pageProps={pageProps}
                getPageLayout={getPageLayout}
              />
            </AuthenticatedThemeModeProvider>
          </RobloxAuthenticationProvider>
        </CacheProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
